import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useTranslation } from "react-i18next"
import { useAuth } from "@/features/auth/AuthContext"
import { tokenStorage } from "@/features/auth/tokenStorage"
import { toast } from "@/features/toast/toast"
import { startNotificationsStream, type SseConnectionState } from "@/features/notifications/sseTransport"
import { startPollingFallback } from "@/features/notifications/pollingFallback"
import {
  deleteAllReadNotifications,
  deleteNotification,
  getNotificationUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/entities/notification/notificationsApi"
import {
  NotificationSeverity,
  type NotificationDto,
  type GetNotificationListInput,
  type NotificationListResponse,
} from "@/entities/notification/types"
import { withAccessTokenRetry } from "@/lib/api/withAccessTokenRetry"
import type { ToastSeverity } from "@/features/toast/types"
import { formatNotificationDisplayFields } from "@/features/notifications/formatDepositNotificationCopy"

const POPOVER_PAGE_SIZE = 10
const BATCH_WINDOW_MS = 1000
const BATCH_THRESHOLD = 10

type NotificationsContextValue = {
  unreadCount: number
  recent: NotificationDto[]
  loading: boolean
  error: string | null
  connectionState: SseConnectionState
  /** Загружает последние N уведомлений в буфер `recent` (для popover колокольчика). */
  refreshRecent: () => Promise<void>
  /** Универсальный фетч для страницы в ЛК: не мутирует состояние провайдера. */
  fetchList: (input?: GetNotificationListInput) => Promise<NotificationListResponse>
  /** `wasUnread` нужен для бейджа, если уведомления нет в буфере `recent` (страница списка в ЛК). */
  markRead: (id: string, options?: { wasUnread?: boolean }) => Promise<void>
  markAllRead: () => Promise<void>
  removeOne: (id: string) => Promise<void>
  clearAllRead: () => Promise<void>
  openNotification: (n: NotificationDto) => void
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null)

const severityToToast = (sev: NotificationDto["severity"]): ToastSeverity => {
  switch (sev) {
    case NotificationSeverity.Success:
      return "success"
    case NotificationSeverity.Warning:
      return "warning"
    case NotificationSeverity.Error:
      return "error"
    default:
      return "info"
  }
}

export const NotificationsProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const { isAuthenticated, accessToken, setSession } = useAuth()

  const [unreadCount, setUnreadCount] = useState(0)
  const [recent, setRecent] = useState<NotificationDto[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [connectionState, setConnectionState] = useState<SseConnectionState>("closed")

  const seenIdsRef = useRef<Set<string>>(new Set())
  const seenEventIdsRef = useRef<Set<number>>(new Set())
  const batchBufferRef = useRef<NotificationDto[]>([])
  const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hiddenBufferRef = useRef<NotificationDto[]>([])

  /** Сразу после POST агрегат /unread-count на бэкенде может ещё отражать старое — не даём подскочить бейджу вверх. */
  const rejectStaleUnreadBumpUntilRef = useRef(0)
  const unreadSyncFirstTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unreadSyncSecondTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unreadSyncThirdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /** ID, для которых идёт пометка прочитанным — не даём SSE с тем же id увеличить бейдж (гонка с markSeen). */
  const pendingReadInflightRef = useRef<Set<string>>(new Set())
  const pendingReadClearTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const clearUnreadSyncTimers = () => {
    if (unreadSyncFirstTimerRef.current !== null) {
      clearTimeout(unreadSyncFirstTimerRef.current)
      unreadSyncFirstTimerRef.current = null
    }
    if (unreadSyncSecondTimerRef.current !== null) {
      clearTimeout(unreadSyncSecondTimerRef.current)
      unreadSyncSecondTimerRef.current = null
    }
    if (unreadSyncThirdTimerRef.current !== null) {
      clearTimeout(unreadSyncThirdTimerRef.current)
      unreadSyncThirdTimerRef.current = null
    }
  }

  const clearPendingReadTimers = () => {
    for (const t of pendingReadClearTimersRef.current.values()) {
      clearTimeout(t)
    }
    pendingReadClearTimersRef.current.clear()
    pendingReadInflightRef.current.clear()
  }

  const registerPendingMarkRead = (id: string) => {
    pendingReadInflightRef.current.add(id)
    const prev = pendingReadClearTimersRef.current.get(id)
    if (prev !== undefined) clearTimeout(prev)
    const t = setTimeout(() => {
      pendingReadClearTimersRef.current.delete(id)
      pendingReadInflightRef.current.delete(id)
    }, 12_000)
    pendingReadClearTimersRef.current.set(id, t)
  }

  const cancelPendingMarkRead = (id: string) => {
    const prev = pendingReadClearTimersRef.current.get(id)
    if (prev !== undefined) {
      clearTimeout(prev)
      pendingReadClearTimersRef.current.delete(id)
    }
    pendingReadInflightRef.current.delete(id)
  }

  /** Живые ссылки, чтобы транспорты не пересоздавались при изменении токена. */
  const accessTokenRef = useRef(accessToken)
  const setSessionRef = useRef(setSession)
  accessTokenRef.current = accessToken
  setSessionRef.current = setSession

  const runAuthed = useCallback(
    <T,>(fn: (token: string) => Promise<T>): Promise<T> => {
      return withAccessTokenRetry(accessTokenRef.current, setSessionRef.current, fn)
    },
    [],
  )

  const markSeen = (n: NotificationDto): boolean => {
    if (seenIdsRef.current.has(n.id) || seenEventIdsRef.current.has(n.eventId)) {
      return false
    }
    seenIdsRef.current.add(n.id)
    seenEventIdsRef.current.add(n.eventId)
    return true
  }

  const fetchList = useCallback(
    async (input: GetNotificationListInput = {}) => {
      return runAuthed((token) => listNotifications(token, input))
    },
    [runAuthed],
  )

  const applyServerUnreadCount = useCallback((count: number) => {
    setUnreadCount((local) => {
      if (Date.now() < rejectStaleUnreadBumpUntilRef.current && count > local) {
        return local
      }
      return count
    })
  }, [])

  const refreshUnreadCount = useCallback(async () => {
    try {
      const { count } = await runAuthed((t) => getNotificationUnreadCount(t))
      applyServerUnreadCount(count)
    } catch {
      /** молча — счётчик подтянется в следующем цикле */
    }
  }, [applyServerUnreadCount, runAuthed])

  /**
   * После мутаций не дергаем GET сразу (часто отдаёт прежнее число).
   * Окно ~4 c: не принимаем с сервера значение строго больше локального — иначе откат бейджа.
   */
  const scheduleUnreadSyncAfterMutation = useCallback(() => {
    rejectStaleUnreadBumpUntilRef.current = Date.now() + 4200
    clearUnreadSyncTimers()
    unreadSyncFirstTimerRef.current = setTimeout(() => {
      unreadSyncFirstTimerRef.current = null
      void refreshUnreadCount()
    }, 600)
    unreadSyncSecondTimerRef.current = setTimeout(() => {
      unreadSyncSecondTimerRef.current = null
      void refreshUnreadCount()
    }, 2800)
    unreadSyncThirdTimerRef.current = setTimeout(() => {
      unreadSyncThirdTimerRef.current = null
      void refreshUnreadCount()
    }, 5500)
  }, [refreshUnreadCount])

  const refreshRecent = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const page = await runAuthed((t) => listNotifications(t, { maxResultCount: POPOVER_PAGE_SIZE }))
      setRecent(page.items)
      /** seed дедупа, чтобы SSE не показывал toast на уже известные. */
      for (const item of page.items) {
        seenIdsRef.current.add(item.id)
        seenEventIdsRef.current.add(item.eventId)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t("notifications.loadError"))
    } finally {
      setLoading(false)
    }
  }, [runAuthed, t])

  const flushBatch = useCallback(() => {
    if (batchTimerRef.current !== null) {
      clearTimeout(batchTimerRef.current)
      batchTimerRef.current = null
    }
    const batch = batchBufferRef.current
    batchBufferRef.current = []
    if (batch.length === 0) return

    if (batch.length >= BATCH_THRESHOLD) {
      toast.custom({
        severity: "info",
        title: t("notifications.toast.aggregated", { count: batch.length }),
      })
      return
    }
    for (const n of batch) {
      const { title, body } = formatNotificationDisplayFields(n, t)
      toast.custom({
        severity: severityToToast(n.severity),
        title,
        description: body ?? undefined,
        onClick: () => {
          void runAuthed((tok) => markNotificationRead(tok, n.id)).catch(() => undefined)
        },
      })
    }
  }, [runAuthed, t])

  const scheduleBatch = useCallback(() => {
    if (batchTimerRef.current !== null) return
    batchTimerRef.current = setTimeout(() => {
      flushBatch()
    }, BATCH_WINDOW_MS)
  }, [flushBatch])

  const handleIncoming = useCallback(
    (dto: NotificationDto, replay: boolean) => {
      const fresh = markSeen(dto)

      /** Всегда обновляем recent-буфер, чтобы popover показывал актуальное. */
      setRecent((prev) => {
        if (prev.some((p) => p.id === dto.id || p.eventId === dto.eventId)) return prev
        return [dto, ...prev].slice(0, POPOVER_PAGE_SIZE)
      })

      /** `pendingReadInflight` гасит эхо-SSE при пометке прочитанным (иначе ложный +1). */
      const blockInflightRead = pendingReadInflightRef.current.has(dto.id)
      if (!replay && dto.isRead !== true && fresh && !blockInflightRead) {
        setUnreadCount((c) => c + 1)
      }

      if (!fresh) return
      if (replay) return

      if (typeof document !== "undefined" && document.hidden) {
        hiddenBufferRef.current.push(dto)
        return
      }

      batchBufferRef.current.push(dto)
      scheduleBatch()
    },
    [scheduleBatch],
  )

  /** Агрегат при возврате в фокус вкладки. */
  useEffect(() => {
    if (!isAuthenticated) return
    const onVisibility = () => {
      if (typeof document === "undefined" || document.hidden) return
      const buffered = hiddenBufferRef.current
      hiddenBufferRef.current = []
      if (buffered.length === 0) return
      if (buffered.length === 1) {
        const n = buffered[0]
        const { title, body } = formatNotificationDisplayFields(n, t)
        toast.custom({
          severity: severityToToast(n.severity),
          title,
          description: body ?? undefined,
          onClick: () => {
            void runAuthed((tok) => markNotificationRead(tok, n.id)).catch(() => undefined)
          },
        })
      } else {
        toast.custom({
          severity: "info",
          title: t("notifications.toast.aggregated", { count: buffered.length }),
        })
      }
      /** Обновим счётчик на всякий случай — на скрытой вкладке могли быть расхождения. */
      void refreshUnreadCount()
    }
    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [isAuthenticated, refreshUnreadCount, runAuthed, t])

  /** Основной цикл: при аутентификации — open SSE + первоначальная загрузка; при ошибке — polling. */
  useEffect(() => {
    if (!isAuthenticated) {
      rejectStaleUnreadBumpUntilRef.current = 0
      clearUnreadSyncTimers()
      clearPendingReadTimers()
      setUnreadCount(0)
      setRecent([])
      seenIdsRef.current.clear()
      seenEventIdsRef.current.clear()
      hiddenBufferRef.current = []
      batchBufferRef.current = []
      if (batchTimerRef.current !== null) {
        clearTimeout(batchTimerRef.current)
        batchTimerRef.current = null
      }
      setConnectionState("closed")
      return
    }

    let cancelled = false
    let pollingController: ReturnType<typeof startPollingFallback> | null = null
    const sse = startNotificationsStream({
      getAccessToken: () => accessTokenRef.current ?? tokenStorage.getAccessToken(),
      setSession: (tokens) => setSessionRef.current(tokens),
      onNotification: (dto, meta) => {
        if (cancelled) return
        handleIncoming(dto, meta.replay)
      },
      onStateChange: (state) => {
        if (!cancelled) setConnectionState(state)
      },
      onFatalError: () => {
        if (cancelled) return
        /** SSE сдался — включаем polling-фолбэк. */
        if (pollingController !== null) return
        pollingController = startPollingFallback({
          getAccessToken: () => accessTokenRef.current ?? tokenStorage.getAccessToken(),
          setSession: (tokens) => setSessionRef.current(tokens),
          onUnreadCount: (count) => {
            if (!cancelled) applyServerUnreadCount(count)
          },
          onNewNotifications: (items) => {
            if (cancelled) return
            for (const dto of items) handleIncoming(dto, false)
          },
        })
      },
    })

    /** Первый seed буфера и счётчика. */
    void refreshRecent()
    void refreshUnreadCount()

    return () => {
      cancelled = true
      sse.stop()
      pollingController?.stop()
      clearUnreadSyncTimers()
      if (batchTimerRef.current !== null) {
        clearTimeout(batchTimerRef.current)
        batchTimerRef.current = null
      }
    }
  }, [applyServerUnreadCount, isAuthenticated, handleIncoming, refreshRecent, refreshUnreadCount])

  const markRead = useCallback(
    async (id: string, options?: { wasUnread?: boolean }) => {
      const row = recent.find((n) => n.id === id)
      const wasUnread =
        options?.wasUnread ?? (row ? !row.isRead : false)
      const prevUnread = wasUnread ? 1 : 0
      if (prevUnread === 1) {
        registerPendingMarkRead(id)
      }

      /** Чтобы последующий SSE не воспринял то же уведомление как новое и не поднял бейдж. */
      const hadSeenId = seenIdsRef.current.has(id)
      const hadSeenEvent = row ? seenEventIdsRef.current.has(row.eventId) : false
      seenIdsRef.current.add(id)
      if (row) seenEventIdsRef.current.add(row.eventId)

      setRecent((prev) =>
        prev.map((n) =>
          n.id === id && !n.isRead
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n,
        ),
      )
      setUnreadCount((c) => Math.max(0, c - prevUnread))
      try {
        await runAuthed((tok) => markNotificationRead(tok, id))
        scheduleUnreadSyncAfterMutation()
      } catch (e) {
        /** Ролбэк. */
        if (prevUnread === 1) {
          cancelPendingMarkRead(id)
        }
        if (!hadSeenId) seenIdsRef.current.delete(id)
        if (row && !hadSeenEvent) seenEventIdsRef.current.delete(row.eventId)
        setUnreadCount((c) => c + prevUnread)
        setRecent((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: false, readAt: null } : n)),
        )
        throw e
      }
    },
    [recent, runAuthed, scheduleUnreadSyncAfterMutation],
  )

  const markAllRead = useCallback(async () => {
    const snapshotRecent = recent
    const snapshotCount = unreadCount
    const unreadIds = recent.filter((n) => !n.isRead).map((n) => n.id)
    for (const uid of unreadIds) {
      registerPendingMarkRead(uid)
    }
    setUnreadCount(0)
    setRecent((prev) =>
      prev.map((n) => (n.isRead ? n : { ...n, isRead: true, readAt: new Date().toISOString() })),
    )
    try {
      await runAuthed((tok) => markAllNotificationsRead(tok))
      scheduleUnreadSyncAfterMutation()
    } catch (e) {
      for (const uid of unreadIds) {
        cancelPendingMarkRead(uid)
      }
      setRecent(snapshotRecent)
      setUnreadCount(snapshotCount)
      throw e
    }
  }, [recent, runAuthed, unreadCount, scheduleUnreadSyncAfterMutation])

  const removeOne = useCallback(
    async (id: string) => {
      const snapshotRecent = recent
      const snapshotCount = unreadCount
      const target = recent.find((n) => n.id === id)
      setRecent((prev) => prev.filter((n) => n.id !== id))
      if (target && !target.isRead) {
        setUnreadCount((c) => Math.max(0, c - 1))
      }
      try {
        await runAuthed((tok) => deleteNotification(tok, id))
        seenIdsRef.current.delete(id)
        if (target) seenEventIdsRef.current.delete(target.eventId)
        scheduleUnreadSyncAfterMutation()
      } catch (e) {
        setRecent(snapshotRecent)
        setUnreadCount(snapshotCount)
        throw e
      }
    },
    [recent, runAuthed, unreadCount, scheduleUnreadSyncAfterMutation],
  )

  const clearAllRead = useCallback(async () => {
    const snapshotRecent = recent
    setRecent((prev) => prev.filter((n) => !n.isRead))
    try {
      await runAuthed((tok) => deleteAllReadNotifications(tok))
    } catch (e) {
      setRecent(snapshotRecent)
      throw e
    }
  }, [recent, runAuthed])

  const openNotification = useCallback((n: NotificationDto) => {
    if (!n.isRead) {
      void markRead(n.id, { wasUnread: true }).catch(() => undefined)
    }
  }, [markRead])

  const value = useMemo<NotificationsContextValue>(
    () => ({
      unreadCount,
      recent,
      loading,
      error,
      connectionState,
      refreshRecent,
      fetchList,
      markRead,
      markAllRead,
      removeOne,
      clearAllRead,
      openNotification,
    }),
    [
      unreadCount,
      recent,
      loading,
      error,
      connectionState,
      refreshRecent,
      fetchList,
      markRead,
      markAllRead,
      removeOne,
      clearAllRead,
      openNotification,
    ],
  )

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components -- useNotifications is a hook, not a component
export const useNotifications = () => {
  const ctx = useContext(NotificationsContext)
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationsProvider")
  }
  return ctx
}
